/**
 * Turns the raw text OCR pulls off a business card into contact fields.
 *
 * OCR output is noisy and cards have no fixed layout, so this is deliberately
 * a best guess: the scanner always confirms and corrects the result before it
 * is saved. Everything it cannot place stays available in the raw text.
 */

export interface ParsedBusinessCard {
  full_name: string;
  job_title: string;
  company: string;
  email: string;
  phone: string;
  website: string;
  address: string;
}

const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
/** Seven or more digits, so street numbers and zip codes don't match. */
const PHONE_RE = /(\+?\(?\d[\d\s().-]{6,}\d)/;
const URL_RE = /\b(?:https?:\/\/)?(?:www\.)[A-Z0-9-]+(?:\.[A-Z0-9-]+)+(?:\/\S*)?/i;
const BARE_DOMAIN_RE =
  /\b[A-Z0-9-]+\.(?:com|net|org|io|co|us|biz|info|health|care|tech|ai)\b(?:\/\S*)?/i;

const TITLE_RE =
  /\b(ceo|cto|coo|cfo|cio|president|vice president|vp|director|manager|supervisor|head of|chief|owner|founder|co-?founder|partner|principal|engineer|architect|consultant|specialist|coordinator|administrator|analyst|representative|rep\.?|account executive|sales|marketing|operations|purchasing|procurement|facilities|environmental services|evs|housekeeping|nurse|rn|md|dds|technician)\b/i;

const COMPANY_RE =
  /\b(inc|inc\.|llc|l\.l\.c\.|ltd|limited|corp|corp\.|corporation|co\.|company|group|holdings|partners|associates|solutions|systems|services|technologies|technology|labs|laboratories|health|healthcare|hospital|medical center|clinic|hotel|resort|university|college|school|district|foundation|institute)\b/i;

const STREET_RE =
  /\b\d{1,6}\s+[\w.'-]+(?:\s+[\w.'-]+)*\s+(street|st\.?|avenue|ave\.?|road|rd\.?|boulevard|blvd\.?|drive|dr\.?|lane|ln\.?|way|court|ct\.?|circle|cir\.?|parkway|pkwy\.?|highway|hwy\.?|suite|ste\.?|floor|fl\.?|unit)\b/i;
/** "Springfield, IL 62704" and similar. */
const CITY_STATE_ZIP_RE = /\b[A-Za-z.'-]+(?:\s+[A-Za-z.'-]+)*,\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?\b/;

const NOISE_RE = /^[^A-Za-z0-9]+$/;

/** OCR loves stray punctuation and doubled spaces; clean each line up. */
function cleanLine(line: string): string {
  return line
    .replace(/\s+/g, ' ')
    .replace(/^[^A-Za-z0-9+(]+/, '')
    .replace(/[|_~^`]+/g, '')
    .trim();
}

/** A person's name: a couple of mostly alphabetic words, no contact details. */
function looksLikeName(line: string): boolean {
  if (line.length < 4 || line.length > 60) return false;
  if (/[@\d]/.test(line)) return false;
  if (COMPANY_RE.test(line) || TITLE_RE.test(line)) return false;
  const words = line.split(' ').filter(Boolean);
  if (words.length < 2 || words.length > 4) return false;
  return words.every((w) => /^[A-Za-z][A-Za-z.'-]*$/.test(w));
}

/** "info@bayviewhotel.com" implies the company is probably Bayview Hotel. */
function companyFromDomain(domain: string): string {
  const base = domain.split('.')[0]?.replace(/[-_]+/g, ' ').trim();
  if (!base || base.length < 3) return '';
  if (/^(gmail|yahoo|hotmail|outlook|icloud|aol|proton|protonmail|live|msn)$/i.test(base)) return '';
  return base.replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

export function parseBusinessCardText(text: string): ParsedBusinessCard {
  const lines = (text || '')
    .split(/\r?\n/)
    .map(cleanLine)
    .filter((l) => l.length > 1 && !NOISE_RE.test(l));

  const joined = lines.join('\n');

  const email = (joined.match(EMAIL_RE)?.[0] ?? '').toLowerCase();

  // Keep the website from swallowing the email's domain.
  const withoutEmail = joined.replace(EMAIL_RE, ' ');
  const websiteMatch = withoutEmail.match(URL_RE) ?? withoutEmail.match(BARE_DOMAIN_RE);
  const website = websiteMatch ? websiteMatch[0].replace(/[.,;]$/, '') : '';

  const phoneLine = lines.find((l) => PHONE_RE.test(l) && !EMAIL_RE.test(l));
  const phoneRaw = phoneLine?.match(PHONE_RE)?.[0] ?? '';
  const phone = phoneRaw.replace(/\s+/g, ' ').trim();

  const jobTitle = lines.find((l) => TITLE_RE.test(l) && !EMAIL_RE.test(l) && l.length < 60) ?? '';

  const fullName = lines.find(looksLikeName) ?? '';

  let company = lines.find((l) => COMPANY_RE.test(l) && l !== jobTitle && l !== fullName) ?? '';
  if (!company && email.includes('@')) company = companyFromDomain(email.split('@')[1] ?? '');
  if (!company && website) company = companyFromDomain(website.replace(/^https?:\/\//i, '').replace(/^www\./i, ''));

  const addressLines = lines.filter((l) => STREET_RE.test(l) || CITY_STATE_ZIP_RE.test(l));

  return {
    full_name: fullName,
    job_title: jobTitle,
    company,
    email,
    phone,
    website,
    address: addressLines.join(', '),
  };
}
